<template>
    <div
        :class="[
            'l-input-typeahead',
            'form-group',
            'label-floating',
            { 'is-focused': isFocused, 'remove-margin': removeMargin },
        ]"
    >
        <label class="control-label" :for="id">
            {{ label }}
        </label>

        <vue-typeahead-bootstrap
            ref="typeahead"
            v-model="query"
            input-class="form-control"
            :placeholder="placeholder"
            :data="filteredData"
            :serializer="serializer"
            show-on-focus
            :show-all-results="showAllResults"
            :max-matches="maxMatches"
            @hit="hit"
        >
            <template v-if="templateType === 'User'" #suggestion="{ data, htmlText }">
                <div
                    data-test-class="typeahead-item"
                    :class="['suggestion', { 'show-picture': showPicture }]"
                >
                    <span v-show="showPicture" class="picture">
                        <l-profile-picture :user="data" size="s" />
                    </span>
                    <i
                        v-show="showCheckbox"
                        class="fal color-primary"
                        :class="data.selected ? 'fa-check-square' : 'fa-square'"
                    ></i>
                    <!-- A real input here doesn't work here, click/select/checked/change events was not fired, just don't even try, I warned you -->
                    <span class="text l-default-margin-s">
                        <span v-html="htmlText"></span>
                        <br />
                        <span class="color-grey">{{ data.desc }} </span>
                    </span>
                </div>
            </template>
            <template v-else #suggestion="{ htmlText }">
                <div data-test-class="typeahead-item" class="suggestion">
                    <div class="text-lg" v-html="htmlText"></div>
                </div>
            </template>
        </vue-typeahead-bootstrap>
    </div>
</template>

<script>
import * as Sentry from "@sentry/vue";

import VueTypeaheadBootstrap from "vue-typeahead-bootstrap";

import LProfilePicture from "@/components/LProfilePicture";
import generateDefaultId from "@/utils/generate-default-id";

export default {
    components: {
        LProfilePicture,
        VueTypeaheadBootstrap,
    },
    props: {
        data: {
            type: Array,
            required: true,
        },
        // Nested named slot usage to define another template is not allowed in Vue
        // which is why we use a condition based on the selected type instead
        templateType: {
            type: String,
            default: "User",
        },
        onSelect: {
            type: Function, // Called when an item from the list is selected
            required: true,
        },
        onInputChange: {
            type: Function, // Called when the input query changes (useful to perform API calls)
            required: true,
        },
        placeholder: {
            type: String,
            default() {
                return this.$t("menu.placeholderSearch");
            },
        },
        label: {
            type: String,
            required: true,
        },
        id: {
            type: String,
            default: generateDefaultId(),
        },
        displaySelectedValue: {
            type: Boolean,
            default: false,
        },
        removeMargin: {
            type: Boolean,
            default: false,
        },
        showPicture: {
            type: Boolean,
            default: true,
        },
        showCheckbox: {
            type: Boolean,
            default: false,
        },
        maxMatches: {
            type: Number,
            default: 40,
        },
        showAllResults: {
            type: Boolean,
            default: false,
        },
        serializer: {
            type: Function,
            default: element => element?.name,
        },
        position: {
            type: String,
            default: "bottom",
        },
    },
    data() {
        return {
            query: "",
            isFocused: false,
        };
    },
    computed: {
        // It happens that we forget to format our data in a correct way which is an error case
        // we want to catch and get notified about
        filteredData() {
            if (!Array.isArray(this.data)) return [];

            const invalidData = [];
            const filteredData = this.data.filter(item => {
                const text = this.serializer(item);
                if (typeof text === "string") return true;

                invalidData.push(item);
                return false;
            });

            if (invalidData.length) {
                const scope = new Sentry.Scope();
                scope.setContext("Leapsome Typeahead", {
                    invalidData: JSON.stringify(invalidData),
                    parentComponent: this.$parent?.$options?._componentTag ?? "Unknown",
                });

                Sentry.captureException(
                    new Error(
                        "Data for Typeahead component is not formatted or a wrong serializer is attached",
                    ),
                    scope,
                );
            }

            return filteredData;
        },
    },
    watch: {
        query(newQuery) {
            this.onInputChange(newQuery);
        },
    },
    mounted() {
        /*
         *  vue-typeahead-bootstrap doesn't provide an API to listen to input events (meaning that we cannot hook up
         *  into @focus or @blur as in LInputText for example), so instead we need to watch the internal state of
         *  the typeahead through references and extract the value of `isFocused` from its internal state.
         *
         *  (cf. https://github.com/alexurquhart/vue-typeahead-bootstrap/blob/master/src/components/VueTypeaheadBootstrap.vue#L166)
         */
        this.$watch(
            () => this.$refs.typeahead.isFocused,
            isFocused => {
                this.isFocused = isFocused;
                if (this.isFocused) {
                    this.$emit("focus");

                    if (this.position === "top") {
                        this.$refs.typeahead.$refs.list.$el.style.top =
                            -1 * this.$refs.typeahead.$refs.list.$el.clientHeight + "px";
                    }
                } else {
                    this.resetQuery();
                    this.$emit("blur");
                }
            },
        );

        if (this.position === "top") {
            this.$watch(
                () => this.$refs.typeahead.$refs.list.matchedItems,
                () => {
                    this.$refs.typeahead.$refs.list.$el.style.top =
                        -1 * this.$refs.typeahead.$refs.list.$el.clientHeight + "px";
                },
            );
        }
    },
    methods: {
        hit(element) {
            this.onSelect(element);
            this.resetQuery();
        },
        resetQuery() {
            if (this.displaySelectedValue) {
                return;
            }

            this.query = "";
            this.$refs.typeahead.inputValue = "";
        },
    },
};
</script>

<style scoped>
.l-input-typeahead {
    margin-top: 10px;
}
.l-input-typeahead.remove-margin {
    margin-top: 0;
}

.l-input-typeahead .suggestion {
    display: flex;
    align-items: center;
}
.l-input-typeahead .suggestion .picture {
    margin-top: 4px;
    margin-left: -5px;
}
.l-input-typeahead .suggestion.show-picture .text {
    margin-left: 10px;
}

.l-input-typeahead >>> .input-group {
    display: block;
}
.l-input-typeahead >>> .vbt-autcomplete-list {
    background-color: #ffffff;
}
/* This is to override the inline styles added by the component ... */
.l-input-typeahead >>> .vbt-autcomplete-list[style] {
    margin-left: 0px !important;
    width: 300px !important;
    padding-top: 0;
    border: 0;
    border-radius: 4px;
    box-shadow: 0 2px 5px 0 rgba(0, 0, 0, 0.26);
}

@media (max-width: 767px) {
    .l-input-typeahead >>> .input-group-prepend {
        display: none;
    }
    .l-input-typeahead >>> .vbt-autcomplete-list[style] {
        margin-left: 30px !important;
        width: 300px !important;
    }
}

.text-lg {
    font-size: 1.25rem;
}
</style>
